import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../Layout';
import { Section, TrailEnd, TrailStart } from '../components/Section';
import { NordketteRidge } from '../components/NordketteRidge';
import { SkillsSection } from '../components/SkillsSection';
import { ProjectDTO } from '../DTO/ProjectDTO';
import { LatestBlogSection } from '../components/LatestBlogSection';
import { FeaturedProjectPreview } from '../components/FeaturedProjectPreview';

const FACTS = [
    { term: 'Based in',  value: 'Innsbruck, Austria' },
    { term: 'Degree',  value: 'BSc Computer Science, University of Innsbruck' },
    { term: 'Working',   value: 'Software developer' },
];

// Drop the photo into /public/me.jpg; initials show as a fallback if it is missing.
const PHOTO_SRC = '/me.jpg';

export default function HomePage() {
    const [featuredProjects, setFeaturedProjects] = useState<ProjectDTO[]>([]);
    const [photoError, setPhotoError] = useState(false);

    useEffect(() => {
        axios.get<ProjectDTO[]>('/api/projects/featured')
            .then(res => setFeaturedProjects(res.data))
            .catch(err => console.error('Failed to fetch featured projects:', err));
    }, []);

    return (
        <Layout>

            {/* ── Hero ──────────────────────────────────────────────────── */}
            <section className="page-container">
                <div className="grid gap-10 pt-10 md:pt-20 pb-4 lg:grid-cols-[1fr_17rem] lg:gap-20">

                    <div>
                        <h1 className="text-[clamp(2.75rem,7vw,4.75rem)] leading-[1.02] mb-6">
                            Manuel Repetschnig
                        </h1>

                        <p className="font-display text-xl md:text-2xl font-medium text-ink mb-5 max-w-[34rem]">
                            Software developer located in Innsbruck, Austria.
                        </p>

                        <p className="text-muted text-lg max-w-[38rem] mb-9">
                            Not a web guy, a software developer who enjoys turning logic into
                            working systems. I like designing robust systems and writing clean,
                            maintainable code, and I learn something new with every project.
                        </p>

                        <div className="flex flex-wrap gap-3 mb-10">
                            <Link to="/projects" className="btn-primary">See my projects</Link>
                            <Link to="/cv" className="btn-secondary">View CV</Link>
                        </div>

                        <dl className="grid gap-x-10 gap-y-3 sm:grid-cols-3 m-0">
                            {FACTS.map(f => (
                                <div key={f.term}>
                                    <dt className="label">{f.term}</dt>
                                    <dd className="m-0 text-ink">{f.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>

                    {/* Mobile: above the name. Desktop: right column, spanning the full height of the text. */}
                    <div className="relative order-first lg:order-none w-32 sm:w-40 aspect-square lg:w-full lg:aspect-auto lg:min-h-[24rem] overflow-hidden rounded border border-rule bg-surface">
                        {photoError ? (
                            <div className="absolute inset-0 grid place-items-center font-display text-6xl font-semibold text-rule select-none">
                                MR
                            </div>
                        ) : (
                            <img
                                src={PHOTO_SRC}
                                alt="Portrait of Manuel Repetschnig"
                                onError={() => setPhotoError(true)}
                                // Small screens: zoom to head and shoulders; desktop: the full portrait.
                                className="absolute inset-0 w-full h-full object-cover object-[center_15%] scale-[1.7] origin-[50%_12%] lg:scale-100"
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* The signature: the range above Innsbruck, then the trail down the page */}
            <NordketteRidge />
            <TrailStart label="Innsbruck, 574 m" />

            <SkillsSection />

            {featuredProjects.length > 0 && (
                <Section
                    title="Featured projects"
                    aside={<Link to="/projects" className="link font-label">All projects</Link>}
                >
                    <div className="grid gap-6 sm:grid-cols-2">
                        {featuredProjects.map(project => (
                            <FeaturedProjectPreview key={project.id} project={project} />
                        ))}
                    </div>
                </Section>
            )}

            <LatestBlogSection />

            <TrailEnd />
            <div className="pb-12" />
        </Layout>
    );
}
